import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Badge,
  Text,
  Spinner,
  EmptyState,
  Pagination,
  Filters,
  ChoiceList,
  TextField,
  Box
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShopifyNavigation } from '../hooks/useShopifyNavigation';
import api from '../utils/axiosConfig';

function Orders() {
  const navigate = useNavigate();
  const navigateWithContext = useShopifyNavigation();
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    next: null,
    previous: null
  });
  
  // Filters
  const [queryValue, setQueryValue] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  
  useEffect(() => {
    // Wait a bit for App Bridge to initialize and URL to be fixed
    const timer = setTimeout(() => {
      loadOrders();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const loadOrders = async (pageInfo = null, filters = {}) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        limit: '20',
        ...(pageInfo && { page_info: pageInfo }),
        ...(filters.status && { status: filters.status }),
        ...(filters.query && { query: filters.query })
      });
      
      const response = await api.get(`/shopify/orders?${params}`);
      
      setOrders(response.data.orders || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (filters) => {
    const newFilters = {
      status: statusFilter.length > 0 ? statusFilter[0] : null,
      query: queryValue
    };
    
    loadOrders(null, newFilters);
  };

  const handleClearFilters = () => {
    setQueryValue('');
    setStatusFilter([]);
    loadOrders();
  };

  const importOrder = async (orderId) => {
    try {
      await api.post(`/shopify/orders/${orderId}/import`);
      
      // Reload orders to reflect changes
      loadOrders();
    } catch (error) {
      console.error('Error importing order:', error);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const locale = i18n.language === 'pt' ? 'pt-BR' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { status: 'warning', content: t('orders.paymentStatus.pending') },
      authorized: { status: 'info', content: t('orders.paymentStatus.authorized') },
      partially_paid: { status: 'warning', content: t('orders.paymentStatus.partially_paid') },
      paid: { status: 'success', content: t('orders.paymentStatus.paid') },
      partially_refunded: { status: 'warning', content: t('orders.paymentStatus.partially_refunded') },
      refunded: { status: 'critical', content: t('orders.paymentStatus.refunded') },
      voided: { status: 'critical', content: t('orders.paymentStatus.voided') }
    };
    
    return statusMap[status] || { status: 'new', content: status };
  };

  const getFulfillmentBadge = (status) => {
    const statusMap = {
      fulfilled: { status: 'success', content: t('orders.fulfillmentStatus.fulfilled') },
      partial: { status: 'warning', content: t('orders.fulfillmentStatus.partial') },
      restocked: { status: 'info', content: t('orders.fulfillmentStatus.restocked') },
      null: { status: 'attention', content: t('orders.fulfillmentStatus.unfulfilled') },
      undefined: { status: 'attention', content: t('orders.fulfillmentStatus.unfulfilled') }
    };
    
    return statusMap[status] || { status: 'new', content: status };
  };

  const tableRows = orders.map((order) => [
    order.name,
    order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : 'N/A',
    formatCurrency(order.total_price, order.currency),
    <Badge {...getStatusBadge(order.financial_status)} />,
    <Badge {...getFulfillmentBadge(order.fulfillment_status)} />,
    formatDate(order.created_at),
    <div style={{ display: 'flex', gap: '8px' }}>
      <Button 
        size="slim" 
        onClick={() => navigate(`/order/${order.id}`)}
      >
        {t('orders.actions.view')}
      </Button>
      <Button 
        size="slim" 
        primary
        onClick={() => importOrder(order.id)}
      >
        {t('orders.actions.import')}
      </Button>
    </div>
  ]);

  const filters = [
    {
      key: 'status',
      label: t('common.status'),
      filter: (
        <ChoiceList
          title={t('orders.paymentStatusFilter')}
          titleHidden
          choices={[
            { label: t('orders.paymentStatus.pending'), value: 'pending' },
            { label: t('orders.paymentStatus.paid'), value: 'paid' },
            { label: t('orders.paymentStatus.refunded'), value: 'refunded' },
            { label: t('orders.paymentStatus.voided'), value: 'voided' }
          ]}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple={false}
        />
      ),
      shortcut: true,
    }
  ];

  const appliedFilters = [];
  
  if (statusFilter.length > 0) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${statusFilter[0]}`,
      onRemove: () => {
        setStatusFilter([]);
        handleFiltersChange();
      }
    });
  }

  if (loading) {
    return (
      <Page title={t('orders.title')}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p" color="subdued">
                  {t('orders.loading')}
                </Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('orders.title')}
      subtitle={t('orders.subtitle')}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Filters
                queryValue={queryValue}
                filters={filters}
                appliedFilters={appliedFilters}
                onQueryChange={setQueryValue}
                onQueryClear={() => setQueryValue('')}
                onClearAll={handleClearFilters}
                onFiltersChange={handleFiltersChange}
              />
            </div>
            
            {orders.length === 0 ? (
              <Box padding="400">
                <EmptyState
                  heading={t('orders.noOrdersFound')}
                  action={{
                    content: t('common.refresh'),
                    onAction: () => loadOrders()
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <Text variant="bodyMd" as="p">
                    {t('orders.noOrdersDescription')}
                  </Text>
                </EmptyState>
              </Box>
            ) : (
              <>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={[t('orders.tableHeaders.order'), t('orders.tableHeaders.customer'), t('orders.tableHeaders.total'), t('orders.tableHeaders.payment'), t('orders.tableHeaders.shipping'), t('orders.tableHeaders.date'), t('orders.tableHeaders.actions')]}
                  rows={tableRows}
                />
                
                {(pagination.next || pagination.previous) && (
                  <div style={{ 
                    padding: '16px', 
                    display: 'flex', 
                    justifyContent: 'center'
                  }}>
                    <Pagination
                      hasPrevious={!!pagination.previous}
                      onPrevious={() => loadOrders(pagination.previous)}
                      hasNext={!!pagination.next}
                      onNext={() => loadOrders(pagination.next)}
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default Orders;